import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Facebook, Twitter, Instagram, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { buildProductsPath } from '../lib/seoUrls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/subscribers/subscribe`, { email: trimmed });
      toast.success(res.data.message || 'Subscribed!');
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Subscription failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#FFFF53] border-t border-[rgba(28,36,30,0.12)] mt-auto" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Subscription box — full-width across the top */}
        <div className="bg-[#1C241E] rounded-2xl p-6 md:p-8 mb-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center" data-testid="footer-subscribe-box">
          <div>
            <h3 className="text-xl md:text-2xl font-medium text-[#FFD700] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('footer.subscribe.heading', 'Get notified about new products')}
            </h3>
            <p className="text-sm text-white/80">
              {t('footer.subscribe.body', 'Be the first to know when we add something new to the store — directly in your inbox.')}
            </p>
          </div>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-2" data-testid="footer-subscribe-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.subscribe.placeholder', 'Your email address')}
              required
              className="flex-1 px-4 py-3 rounded-full text-sm bg-white text-[#1C241E] placeholder:text-[#5C6B61] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]"
              data-testid="footer-subscribe-email"
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-full bg-[#D96C4A] text-white font-semibold text-sm hover:bg-[#C05A3D] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="footer-subscribe-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? t('common.sending', 'Sending…') : t('footer.subscribe.cta', 'Subscribe')}
            </button>
          </form>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center space-x-2">
              <img src="/logo.png" alt="VEEVAMMERCE" className="h-12 w-auto object-contain rounded-md" />
              <span className="text-xl font-bold text-[#1C241E]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <span className="text-[#D4A017]">{t('brand.name', 'VEEVAMMERCE')}</span> <span className="text-[#4A5568]">{t('brand.store', 'STORE')}</span>
              </span>
            </Link>
            <p className="text-[#1C241E] text-sm">
              {t('brand.tagline', 'Your First Choice for Best Products')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-[#1C241E] hover:text-[#D96C4A]"><Facebook className="w-5 h-5" /></a>
              <a href="#" className="text-[#1C241E] hover:text-[#D96C4A]"><Twitter className="w-5 h-5" /></a>
              <a href="#" className="text-[#1C241E] hover:text-[#D96C4A]"><Instagram className="w-5 h-5" /></a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-medium text-[#1C241E] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('footer.quickLinks', 'Quick Links')}
            </h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('nav.home', 'Home')}</Link></li>
              <li><Link to="/products" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('nav.products', 'Products')}</Link></li>
              <li><Link to="/about" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('nav.about', 'About Us')}</Link></li>
              <li><Link to="/orders" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('nav.orders', 'My Orders')}</Link></li>
              <li><Link to="/contact" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('footer.contactUs', 'Contact Us')}</Link></li>
              <li><Link to="/terms" className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('footer.terms', 'Terms & Policy')}</Link></li>
              <li><Link to="/affiliate" className="text-[#1C241E] hover:text-[#D96C4A] text-sm" data-testid="footer-affiliate-link">{t('aff.becomeAffiliate', 'Become an Affiliate')}</Link></li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-lg font-medium text-[#1C241E] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('categories.title', 'Shop by Category')}
            </h3>
            <ul className="space-y-2">
              <li><Link to={buildProductsPath(lang, 'home-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.home-products', 'Home Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'travel-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.travel-products', 'Travel Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'decoration')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.decoration', 'Decoration Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'kids-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.kids-products', 'Kids Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'pet-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.pet-products', 'Pet Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'camping-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.camping-products', 'Camping Products')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'car-accessories')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.car-accessories', 'Car Accessories')}</Link></li>
              <li><Link to={buildProductsPath(lang, 'digital-products')} className="text-[#1C241E] hover:text-[#D96C4A] text-sm">{t('cat.digital-products', 'Digital Products')}</Link></li>
              <li><Link to={buildProductsPath(lang)} className="text-[#D96C4A] hover:underline text-sm font-semibold">{t('categories.viewAll', 'View all categories →')}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-medium text-[#1C241E] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('footer.contactUs', 'Contact Us')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#1C241E] mt-0.5 flex-shrink-0" />
                <span className="text-[#1C241E] text-sm">info@veevammerce.com</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#1C241E] mt-0.5 flex-shrink-0" />
                <span className="text-[#1C241E] text-sm">Wyoming State, USA</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[rgba(28,36,30,0.12)] mt-8 pt-8 text-center">
          <p className="text-[#1C241E] text-sm">
            &copy; {new Date().getFullYear()} VEEVAMMERCE. {t('footer.rights', 'All rights reserved.')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
